import React, { useMemo } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import styles from "./VendorManagement.module.css";
import CommonButton from "../../CommonComponents/CommonButton";
import CommonInput from "../../CommonComponents/CommonInput";
import CommonSelect from "../../CommonComponents/Select/CommonSelect";
import { useEditVendorDetails } from "./useEditVendorDetails";
import { clubOptions, companySizeOptions, LOB, lobOptions, SPECIALISED_OPTIONS } from "../../Utils/EditVendorModal.utils";

const FieldError = ({ error }) =>
  error ? (
    <div className={styles["errorText"]}>{error}</div>
  ) : null;

const EditVendorDetails = ({
  vendorId,
  vendorDetails,
  onCancel,
  onSaved,
}) => {
  const {
    formData,
    formErrors,
    stateOptions,
    cityOptions,
    availableSubProductOptions,
    highValueOptions,
    isSaving,
    handleFieldChange,
    handleStateChange,
    handleSpecialisedChange,
    handleLobChange,
    handleSubProductChange,
    handleHighValueChange,
    handleSave,
  } = useEditVendorDetails({
    vendorId,
    vendorDetails,
    onSaved,
  });

  const items = useMemo(
    () => [
      { label: "Company Name", name: "company_name" },
      { label: "GC Code", name: "gc_code" },
      { label: "LOB", name: "lob_id" },
      { label: "PAN Number", name: "pan_number" },
      { label: "GST Number", name: "gst_number" },
      { label: "State", name: "state" },
      { label: "City", name: "city" },
      { label: "Status", name: "status" },
      { label: "Club", name: "club" },
      { label: "Company Size", name: "company_size" },
      { label: "Specialised", name: "specialised" },
      ...(formData.lob_id !== LOB.HEALTH
        ? [
          {
            label: "Sub Product's",
            name: "sub_product_id",
          },
        ]
        : []),
      { label: "High Value", name: "high_value" },
      { label: "Capacity", name: "capacity" },
      { label: "Employee Name", name: "employee_name" },
      { label: "Employee Mobile", name: "employee_mobile" },
      { label: "Employee Email", name: "employee_email" },
    ],
    [formData.lob_id]
  );

  const renderEditField = (item) => {
    if (item.name === "state") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            name="state"
            value={formData.state ?? ""}
            options={stateOptions.map(
              (option) => option.state_name
            )}
            placeholder="Select State"
            onChange={handleStateChange}
            error={formErrors.state}
          />
        </div>
      );
    }

    if (item.name === "city") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            name="city"
            value={formData.city ?? ""}
            options={cityOptions}
            placeholder="Select City"
            onChange={handleFieldChange}
            error={formErrors.city}
          />
        </div>
      );
    }

    if (item.name === "gc_code") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonInput
            name="gc_code"
            value={formData.gc_code ?? ""}
            placeholder="Enter GC Code"
            onChange={handleFieldChange}
            readOnly
            removeBackGround
            error={formErrors.gc_code}
          />
        </div>
      );
    }

    if (item.name === "status") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonInput
            name="status"
            value={formData.status ?? ""}
            placeholder="Enter Status"
            onChange={handleFieldChange}
            readOnly
            removeBackGround
            error={formErrors.status}
          />
        </div>
      );
    }

    if (item.name === "company_size") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            name="company_size"
            value={formData.company_size ?? ""}
            options={companySizeOptions}
            placeholder="Select Company Size"
            onChange={handleFieldChange}
            error={formErrors.company_size}
          />
        </div>
      );
    }

    if (item.name === "club") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            name="club"
            value={formData.club ?? ""}
            options={clubOptions}
            placeholder="Select Club"
            onChange={handleFieldChange}
            error={formErrors.club}
          />
        </div>
      );
    }

    if (item.name === "specialised") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            name="specialised"
            value={
              formData.specialised === "yes"
                ? "Yes"
                : formData.specialised === "no"
                  ? "No"
                  : ""
            }
            options={SPECIALISED_OPTIONS}
            placeholder="Select Specialised"
            onChange={handleSpecialisedChange}
            error={formErrors.specialised}
          />
        </div>
      );
    }

    if (item.name === "lob_id") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            name="lob_id"
            value={formData.lob_id ?? ""}
            options={lobOptions}
            placeholder="Select LOB"
            onChange={handleLobChange}
            error={formErrors.lob_id}
          />
        </div>
      );
    }

    if (item.name === "sub_product_id") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            isMulti
            name="sub_product_id"
            value={formData.sub_product_id ?? []}
            options={availableSubProductOptions}
            placeholder="Select Sub Product"
            onChange={handleSubProductChange}
            error={formErrors.sub_product_id}
          />
        </div>
      );
    }

    if (item.name === "capacity") {
      return (
        <div className={styles["capacityStack"]}>
          {formData.lob_id === LOB.MOTOR && (
            <div className={styles["fieldWrapper"]}>
              <CommonInput
                name="capacity_motor"
                value={formData.capacity_motor ?? ""}
                placeholder="Enter Motor Capacity"
                onChange={handleFieldChange}
                removeBackGround
                error={formErrors.capacity_motor}
              />
            </div>
          )}

          {formData.lob_id === LOB.HEALTH && (
            <div className={styles["fieldWrapper"]}>
              <CommonInput
                name="capacity_health"
                value={formData.capacity_health ?? ""}
                placeholder="Enter Health Capacity"
                onChange={handleFieldChange}
                removeBackGround
                error={formErrors.capacity_health}
              />
            </div>
          )}
        </div>
      );
    }

    if (item.name === "high_value") {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonSelect
            showLabel={false}
            isMulti
            name="high_value"
            value={formData.high_value ?? []}
            options={highValueOptions}
            placeholder={
              formData.lob_id === LOB.MOTOR &&
                !formData.sub_product_id.length
                ? "Select Sub Product first"
                : "Select High Value"
            }
            disabled={!highValueOptions.length}
            onChange={(event) =>
              handleHighValueChange(
                event,
                highValueOptions
              )
            }
            error={formErrors.high_value}
          />
        </div>
      );
    }

    if (
      [
        "pan_number",
        "gst_number",
        "mobile_number",
        "email",
      ].includes(item.name)
    ) {
      return (
        <div className={styles["fieldWrapper"]}>
          <CommonInput
            name={item.name}
            value={formData[item.name] ?? ""}
            placeholder={`Enter ${item.label}`}
            onChange={handleFieldChange}
            removeBackGround
            error={formErrors[item.name]}
          />
        </div>
      );
    }

    return (
      <div className={styles["fieldWrapper"]}>
        <CommonInput
          name={item.name}
          value={formData[item.name] ?? ""}
          placeholder={`Enter ${item.label}`}
          onChange={handleFieldChange}
          removeBackGround
          error={formErrors[item.name]}
        />
      </div>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        {items.map((item) => {
          const errorKey =
            item.name === "capacity"
              ? formData.lob_id === LOB.MOTOR
                ? "capacity_motor"
                : formData.lob_id === LOB.HEALTH
                  ? "capacity_health"
                  : ""
              : item.name;
          return (
            <Grid
              item
              xs={12}
              sm={6}
              key={item.label}
            >
              <Box className={styles.detailItem}>
                <Typography
                  className={styles.detailLabel}
                >
                  {item.label}
                </Typography>
                {item.name &&
                  renderEditField(item)}
                {errorKey && (
                  <FieldError
                    error={formErrors[errorKey]}
                  />
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        className={styles.editActionRow}
      >
        <CommonButton
          label="Cancel"
          onClick={onCancel}
          disabled={isSaving}
          removeBorder
          fitContent
          className={styles.cancelButton}
        />
        <CommonButton
          label={isSaving ? "Saving..." : "Save"}
          onClick={handleSave}
          disabled={isSaving}
          filledButton
          fitContent
          className={styles.saveButton}
        />
      </Stack>
    </>
  );
};

export default EditVendorDetails;